/**
 * Démarrer un échange avec des élèves : recherche par nom, puis échange à
 * deux ou groupe avec des membres de l'équipe.
 */
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Check, Loader2, Search, User, X } from 'lucide-react';
import {
  MAX_GROUP_TITLE_LENGTH,
  MIN_STUDENT_SEARCH_LENGTH,
  studentMessagingApi,
  studentMessagingError,
  studentMessagingKeys,
  type StaffContact,
  type StaffRole,
  type StudentContact,
} from '../api/studentMessaging';
import { useAuthStore } from '../store/auth';
import {
  OFFLINE,
  card,
  field,
  primaryButton,
  secondaryButton,
} from './StudentMessageParts';

const ROLE_LABELS: Record<StaffRole, string> = {
  owner: 'Gérant',
  manager: 'Responsable',
  agent: 'Secrétariat',
  read_only: 'Lecture seule',
  instructor: 'Moniteur',
};

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Recherche d'un élève de l'école par son nom, à partir de deux caractères. */
function StudentSearch({
  label,
  picked = [],
  busyId = null,
  onPick,
}: {
  label: string;
  picked?: string[];
  busyId?: string | null;
  onPick: (student: StudentContact) => void;
}) {
  const userId = useAuthStore((s) => s.user?.id);
  const [value, setValue] = useState('');
  const typed = value.trim();
  const term = useDebounced(typed, 300);
  const ready =
    typed.length >= MIN_STUDENT_SEARCH_LENGTH &&
    term.length >= MIN_STUDENT_SEARCH_LENGTH;
  const results = useQuery({
    queryKey: studentMessagingKeys.students(userId, term),
    queryFn: () => studentMessagingApi.searchStudents(term),
    enabled: term.length >= MIN_STUDENT_SEARCH_LENGTH,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });
  const settled = ready && typed === term && !results.isFetching;

  return (
    <div>
      <label className="block">
        <span className="block text-sm font-medium text-slate-700 mb-1.5">
          {label}
        </span>
        <span className="relative block">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="text"
            enterKeyHint="search"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            // Entrée ne valide pas le formulaire qui contient la recherche.
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder="Nom de l’élève"
            autoComplete="off"
            maxLength={120}
            className={`${field} pl-9 pr-9`}
          />
          {ready && results.isFetching && (
            <Loader2
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin"
            />
          )}
        </span>
      </label>
      {typed.length < MIN_STUDENT_SEARCH_LENGTH && (
        <p className="text-xs text-slate-500 mt-1.5">
          Saisissez au moins {MIN_STUDENT_SEARCH_LENGTH} caractères du nom.
        </p>
      )}
      {settled && results.isError && (
        <p role="alert" className="text-sm text-red-600 mt-2">
          {studentMessagingError(results.error, OFFLINE)}
        </p>
      )}
      {settled && !results.isError && results.data?.length === 0 && (
        <p className="text-sm text-slate-500 mt-2">Aucun élève trouvé.</p>
      )}
      {ready && !results.isError && !!results.data?.length && (
        <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
          {results.data.map((student) => {
            const added = picked.includes(student.id);
            return (
              <li key={student.id}>
                <button
                  type="button"
                  disabled={added || busyId !== null}
                  onClick={() => onPick(student)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-slate-50 disabled:hover:bg-white disabled:opacity-60"
                >
                  <span
                    aria-hidden="true"
                    className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0"
                  >
                    <User size={15} />
                  </span>
                  <span className="flex-1 min-w-0 truncate text-slate-900">
                    {student.name}
                  </span>
                  {added && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 flex-shrink-0">
                      <Check size={14} />
                      Ajouté
                    </span>
                  )}
                  {busyId === student.id && (
                    <Loader2
                      size={15}
                      className="animate-spin text-slate-400 flex-shrink-0"
                    />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Panel({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className={card}>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
        <h2 className="flex-1 min-w-0 text-base font-semibold text-slate-900">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="p-2 -mr-1 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
        >
          <X size={18} />
        </button>
      </div>
      <div className="p-4 space-y-4">{children}</div>
    </div>
  );
}

/** Ouvrir un échange à deux avec un élève trouvé par son nom. */
export function NewDirectPanel({
  onClose,
  onOpened,
}: {
  onClose: () => void;
  onOpened: (conversationId: string) => void;
}) {
  const open = useMutation({
    mutationFn: (student: StudentContact) =>
      studentMessagingApi.openDirect(student.id),
    onSuccess: ({ id }) => onOpened(id),
  });
  return (
    <Panel title="Nouvel échange" onClose={onClose}>
      <p className="text-sm text-slate-600">
        Choisissez un élève de votre auto-école : l’échange reste entre vous
        deux.
      </p>
      <StudentSearch
        label="Rechercher un élève"
        busyId={open.isPending ? (open.variables?.id ?? null) : null}
        onPick={(student) => open.mutate(student)}
      />
      {open.isError && (
        <p role="alert" className="text-sm text-red-600">
          {studentMessagingError(open.error, OFFLINE)}
        </p>
      )}
    </Panel>
  );
}

/** Créer un groupe : un nom, des élèves, des membres de l'équipe. */
export function NewGroupPanel({
  staff,
  onClose,
  onCreated,
}: {
  staff: StaffContact[];
  onClose: () => void;
  onCreated: (conversationId: string) => void;
}) {
  const me = useAuthStore((s) => s.user?.id);
  const [title, setTitle] = useState('');
  const [students, setStudents] = useState<StudentContact[]>([]);
  const [staffIds, setStaffIds] = useState<string[]>([]);
  // Un nouvel essai de la même demande reprend son identifiant : pas de doublon.
  const pending = useRef({ signature: '', requestId: '' });
  const create = useMutation({
    mutationFn: () => {
      const payload = {
        title: title.trim(),
        staffIds,
        candidateIds: students.map((s) => s.id),
      };
      const signature = JSON.stringify(payload);
      if (pending.current.signature !== signature)
        pending.current = { signature, requestId: crypto.randomUUID() };
      return studentMessagingApi.createGroup({
        requestId: pending.current.requestId,
        ...payload,
      });
    },
    onSuccess: ({ id }) => onCreated(id),
  });
  // Le serveur ajoute lui-même la personne qui crée le groupe.
  const colleagues = staff.filter((person) => person.id !== me);
  const valid = title.trim().length > 0 && students.length > 0;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || create.isPending) return;
    create.mutate();
  }

  return (
    <Panel title="Nouveau groupe" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-600">
          Vous en faites partie d’office. Sa composition est fixée à la
          création.
        </p>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700 mb-1.5">
            Nom du groupe
          </span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={MAX_GROUP_TITLE_LENGTH}
            required
            className={field}
          />
        </label>

        <div className="space-y-2">
          <StudentSearch
            label="Ajouter des élèves"
            picked={students.map((s) => s.id)}
            onPick={(student) =>
              setStudents((list) =>
                list.some((s) => s.id === student.id)
                  ? list
                  : [...list, student],
              )
            }
          />
          {students.length === 0 ? (
            <p className="text-xs text-slate-500">
              Un groupe comprend au moins un élève.
            </p>
          ) : (
            <ul aria-label="Élèves du groupe" className="flex flex-wrap gap-2">
              {students.map((student) => (
                <li
                  key={student.id}
                  className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1 rounded-full bg-indigo-50 text-indigo-700 text-sm"
                >
                  {student.name}
                  <button
                    type="button"
                    aria-label={`Retirer ${student.name}`}
                    onClick={() =>
                      setStudents((list) =>
                        list.filter((s) => s.id !== student.id),
                      )
                    }
                    className="p-1 rounded-full hover:bg-indigo-100"
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-slate-700 mb-1.5">
            Membres de l’équipe
          </legend>
          {colleagues.length === 0 ? (
            <p className="text-xs text-slate-500">
              Aucun autre membre de l’équipe à ajouter.
            </p>
          ) : (
            <ul className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
              {colleagues.map((person) => (
                <li key={person.id}>
                  <label className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 flex-shrink-0 accent-indigo-600"
                      checked={staffIds.includes(person.id)}
                      onChange={(e) =>
                        setStaffIds((ids) =>
                          e.target.checked
                            ? [...ids, person.id]
                            : ids.filter((id) => id !== person.id),
                        )
                      }
                    />
                    <span className="flex-1 min-w-0 truncate text-slate-900">
                      {person.name}
                    </span>
                    <span className="flex-shrink-0 text-xs text-slate-500">
                      {ROLE_LABELS[person.role] ?? ''}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </fieldset>

        {create.isError && (
          <p role="alert" className="text-sm text-red-600">
            {studentMessagingError(create.error, OFFLINE)}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            className={primaryButton}
            disabled={!valid || create.isPending}
          >
            {create.isPending && <Loader2 size={15} className="animate-spin" />}
            Créer le groupe
          </button>
          <button type="button" className={secondaryButton} onClick={onClose}>
            Annuler
          </button>
        </div>
      </form>
    </Panel>
  );
}
